/**
 * 开票主体状态监控与告警 —— 本地静态数据
 *
 * 说明：状态看板（KPI / 地图 / 异常清单 / 告警时间线）与 IE 信息管理的数据
 *      全部来自后端接口，接口未通 / 请求失败时页面按空态展示，不再回退假数据。
 *
 * 本文件只保留静态配置与字典：州 UF 列表、曝光天数、状态枚举与变更字段的中葡文案、日期工具。
 * 各模块的业务数据（看板 / IE 管理 / 告警配置 / 变更日志 / 告警日志）全部来自接口，无本地兜底数据。
 */

// ==================== 日期工具 ====================
function pad(n) {
  return String(n).padStart(2, '0');
}

/** 今天 00:00:00 */
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** 当前时间字符串 MM-DD HH:mm:ss（用于新增 / 编辑后的「最后检查时间」与变更日志） */
export function nowStamp() {
  const d = new Date();
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** 今天 yyyy-MM-dd（查询条件里时间区间的默认值） */
export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ==================== CNPJ 工具 ====================
// 输入 / 展示口径：系统内统一按「00.000.000/0000-00」展示，提交与查询一律用去掉符号的 14 位
/**
 * CNPJ 输入格式：严格整体匹配，只接受两种写法
 *   1. 00.000.000/0000-00（带符号，字母数字均可，末两位为数字）
 *   2. 14 位无符号（前 12 位字母或数字，末两位为数字）
 * 注意：两个分支必须包在同一分组内，否则会退化成 `(^A)|(B$)`，出现「只要末两位是数字且长度 ≥14 就通过」的误判
 */
export const CNPJ_INPUT_RE = /^(?:[0-9A-Za-z]{2}\.[0-9A-Za-z]{3}\.[0-9A-Za-z]{3}\/[0-9A-Za-z]{4}-\d{2}|[0-9A-Za-z]{12}\d{2})$/;

/** 去掉符号，只保留数字与字母（提交接口、查询入参都用无符号格式） */
export function cnpjDigits(value) {
  return String(value || '').replace(/[^0-9a-zA-Z]/g, '');
}

/** 无符号 14 位 → 00.000.000/0000-00（列表展示）；非 14 位原样返回 */
export function fmtCnpj(value) {
  const digits = cnpjDigits(value);
  if (digits.length !== 14) return value || '';
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** 距离有效期限剩余天数；未录入 / 非法日期返回 null */
export function daysLeft(valStr) {
  if (!valStr) return null;
  const d = new Date(`${valStr}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  return Math.round((d - startOfToday()) / 86400000);
}

// ==================== 状态字典（枚举值 → 中葡文案；枚举值与接口保持一致） ====================
/** 税务状态 taxStatus：1有效 2无效 3未知 4未录入 */
export const TAX_LABEL = {
  1: { zh: '有效', pt: 'Válida', cls: 'ok' },
  2: { zh: '无效', pt: 'Inválida', cls: 'err' },
  3: { zh: '未知', pt: 'Desconhecido', cls: 'warn' },
  4: { zh: '未录入', pt: 'Não Registrado', cls: 'pending' }
};

/** 系统配置状态 sysStatus：1已配置 2未配置 */
export const SYS_LABEL = {
  1: { zh: '已配置', pt: 'Configurado', cls: 'ok' },
  2: { zh: '未配置', pt: 'Não Configurado', cls: 'err' }
};

/** 告警类型 alertType：1紧急 2恢复 3警告（时间线 / 告警日志共用，kind 用于配色） */
export const ALERT_TYPE_LABEL = {
  1: { zh: '紧急告警', pt: 'Crítico', cls: 'tag-err', kind: 'err' },
  2: { zh: '恢复通知', pt: 'Recuperação', cls: 'tag-ok', kind: 'ok' },
  3: { zh: '警告通知', pt: 'Aviso', cls: 'tag-warn', kind: 'warn' }
};

/** 发送状态 sendStatus：1成功 2失败 */
export const SEND_STATUS_LABEL = {
  1: { zh: '成功', pt: 'Sucesso', cls: 'tag-ok' },
  2: { zh: '失败', pt: 'Falha', cls: 'tag-err' }
};

/** 检查来源 lastCheckSource / source / triggerType：1定时任务 2手动触发 */
export const CHECK_SOURCE_LABEL = {
  1: { zh: '定时任务', pt: 'Tarefa agendada' },
  2: { zh: '手动触发', pt: 'Manual' }
};

/** 变更字段 changeField：1税务 2系统 3启用 4有效期 5基础信息 */
export const CHANGE_FIELD_LABEL = {
  1: { zh: '税务状态', pt: 'Status Fiscal', cls: 'tag-err' },
  2: { zh: '系统配置', pt: 'Config. do Sistema', cls: 'tag-info' },
  3: { zh: '启用状态', pt: 'Ativação', cls: 'tag-warn' },
  4: { zh: '有效期限', pt: 'Validade', cls: 'tag-brand' },
  5: { zh: '基础信息', pt: 'Dados Básicos', cls: 'tag-gray' }
};

/** 配置类型 configObject（IeConfigObjectEnum）：1接收人 2模板 3州范围 */
export const CONFIG_OBJECT_LABEL = {
  1: { zh: '告警接收人', pt: 'Destinatários de Alerta', cls: 'tag-info' },
  2: { zh: '告警文案', pt: 'Modelos de Alerta', cls: 'tag-brand' },
  3: { zh: '监控州配置', pt: 'Escopo de Estados', cls: 'tag-warn' }
};

/** 操作类型 action（IeConfigActionEnum）：1新增 2编辑 3启停 4删除 */
export const CONFIG_ACTION_LABEL = {
  1: { zh: '新增', pt: 'Criação', cls: 'tag-ok' },
  2: { zh: '编辑', pt: 'Edição', cls: 'tag-info' },
  3: { zh: '启停', pt: 'Ativar/Desativar', cls: 'tag-warn' },
  4: { zh: '删除', pt: 'Exclusão', cls: 'tag-err' }
};

